import { useState } from "react";
import { fetchSassTokenApi } from "../../api/toolboxExerciseApi";
import { Spinner } from "react-bootstrap";
import ReactPlayer from "react-player";

const extractBlobPath = (url) => {
  const cleanUrl = url.split("?")[0];
  const parts = cleanUrl.split("/apexmediafiles/");
  return parts[1] || "";
};

// Video Component with On-Click Fetch and Play
export const VideoWithOnClickFetch = ({
  parsedFile,
  width = "250px",
  height = "190px",
}) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleFetchAndPlay = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    if (isFetching) return;

    setIsFetching(true);
    setHasError(false);

    try {
      const fileName = extractBlobPath(parsedFile.mediaUrl);
      const res = await fetchSassTokenApi({ fileName });

      const newUrl =
        res?.uploadUrl ||
        res?.mediaUrl ||
        res?.url ||
        (res?.data &&
          (res.data.uploadUrl || res.data.mediaUrl || res.data.url));
      if (!newUrl) throw new Error("No SAS URL returned");

      setVideoUrl(newUrl);
    } catch (err) {
      setHasError(true);
    } finally {
      setIsFetching(false);
    }
  };

  if (videoUrl) {
    return (
      <ReactPlayer
        url={videoUrl}
        controls
        width={width}
        height={height}
        onError={(e) => console.log("Player error (ignored):", e)}
      />
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Play ${parsedFile?.fileName}`}
      style={{
        width,
        height,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isFetching ? "not-allowed" : "pointer",
        borderRadius: "12px",
        overflow: "hidden",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
      }}
      onClick={handleFetchAndPlay}
      onMouseDown={handleFetchAndPlay}
      onTouchStart={handleFetchAndPlay}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleFetchAndPlay(e);
        }
      }}
    >
      {/* Background Pattern */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `
            repeating-linear-gradient(
              45deg,
              rgba(255, 255, 255, 0.03) 0px,
              rgba(255, 255, 255, 0.03) 10px,
              transparent 10px,
              transparent 20px
            )
          `,
          pointerEvents: "none",
        }}
      />

      {/* Content Container */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          pointerEvents: "none",
        }}
      >
        {/* Video Icon */}
        <div
          style={{
            width: "70px",
            height: "70px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.25)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
            border: "2px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          {isFetching ? (
            <Spinner animation="border" size="sm" style={{ color: "white" }} />
          ) : (
            <svg
              viewBox="0 0 24 24"
              width="32"
              height="32"
              fill="white"
              style={{
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                marginLeft: "4px",
              }}
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </div>

        {/* Click to Play Text */}
         {!isFetching && (
          <p
            style={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "11px",
              fontWeight: "600",
              margin: 0,
              textTransform: "uppercase",
              letterSpacing: "1px",
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            Tab to Play Video
          </p>
        )} 
      </div>

      {/* Error Message */}
      {hasError && (
        <div
          style={{
            position: "absolute",
            bottom: 8,
            left: 8,
            right: 8,
            textAlign: "center",
            background: "rgba(239, 68, 68, 0.95)",
            backdropFilter: "blur(8px)",
            borderRadius: "8px",
            padding: "8px 12px",
            color: "white",
            fontSize: "11px",
            fontWeight: "600",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          }}
        >
          <i className="bi bi-exclamation-triangle me-1"></i>
          Error loading video — tap to retry
        </div>
      )}
    </div>
  );
};

// audio component with on-click fetch and play
export const AudioWithOnClickFetch = ({
  parsedFile,
  width = "250px",
  height = "190px",
}) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const handleFetchAndPlay = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    if (isFetching) return;

    setIsFetching(true);

    try {
      const fileName = extractBlobPath(parsedFile.mediaUrl);
      const res = await fetchSassTokenApi({ fileName });

      const newUrl =
        res?.uploadUrl ||
        res?.mediaUrl ||
        res?.url ||
        (res?.data &&
          (res.data.uploadUrl || res.data.mediaUrl || res.data.url));

      if (!newUrl) throw new Error("No SAS URL returned for audio");

      setAudioUrl(newUrl);
    } catch (err) {
    } finally {
      setIsFetching(false);
    }
  };

  if (audioUrl) {
    return (
      <div
        style={{
          width: "100%",
          maxWidth: "320px",
          background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
          borderRadius: "12px",
          padding: "16px",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
        }}
      >
        {/* File Name Header */}
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i
            className="bi bi-music-note-beamed"
            style={{
              fontSize: "18px",
              color: "white",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
            }}
          ></i>
          <p
            style={{
              color: "white",
              fontSize: "13px",
              fontWeight: "600",
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            {parsedFile.fileName || "Audio File"}
          </p>
        </div>

        {/* Audio Player */}
        <audio
          controls
          autoPlay
          style={{
            width: "100%",
            height: "40px",
            outline: "none",
          }}
          src={audioUrl}
          onError={(e) => console.log("Audio player error (ignored):", e)}
        />
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Play ${parsedFile?.fileName}`}
      style={{
        width,
        height,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isFetching ? "not-allowed" : "pointer",
        borderRadius: "12px",
        overflow: "hidden",
        background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
      }}
      onClick={handleFetchAndPlay}
      onMouseDown={handleFetchAndPlay}
      onTouchStart={handleFetchAndPlay}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleFetchAndPlay(e);
        }
      }}
    >
      {/* Background Pattern */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `
            repeating-linear-gradient(
              45deg,
              rgba(255, 255, 255, 0.03) 0px,
              rgba(255, 255, 255, 0.03) 10px,
              transparent 10px,
              transparent 20px
            )
          `,
          pointerEvents: "none",
        }}
      />

      {/* Content Container */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          pointerEvents: "none",
        }}
      >
        {/* Audio Icon Circle with Play Button */}
        <div
          style={{
            width: "70px",
            height: "70px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.25)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
            border: "2px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          {isFetching ? (
            <Spinner animation="border" size="sm" style={{ color: "white" }} />
          ) : (
            <svg
              viewBox="0 0 24 24"
              width="32"
              height="32"
              fill="white"
              style={{
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                marginLeft: "4px",
              }}
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </div>

        {/* Click to Play Text */}
        {!isFetching && (
          <p
            style={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "11px",
              fontWeight: "600",
              margin: 0,
              textTransform: "uppercase",
              letterSpacing: "1px",
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            Click to Play Audio
          </p>
        )}
      </div>
    </div>
  );
};

// image component with on-click fetch and display
export const ImageWithOnClickFetch = ({
  parsedFile,
  width = "200px",
  height = "180px",
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleFetchAndLoad = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    if (isFetching) return;

    setIsFetching(true);

    try {
      const fileName = extractBlobPath(parsedFile.mediaUrl);
      const res = await fetchSassTokenApi({ fileName });

      const newUrl =
        res?.uploadUrl ||
        res?.mediaUrl ||
        res?.url ||
        (res?.data &&
          (res.data.uploadUrl || res.data.mediaUrl || res.data.url));

      if (!newUrl) throw new Error("No SAS URL returned for image");

      setImageUrl(newUrl);
    } catch (err) {
    } finally {
      setIsFetching(false);
    }
  };

  const finalUrl = imageUrl || parsedFile.mediaUrl;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Load ${parsedFile?.fileName}`}
      onClick={handleFetchAndLoad}
      onMouseDown={handleFetchAndLoad}
      onTouchStart={handleFetchAndLoad}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleFetchAndLoad(e);
        }
      }}
      style={{
        width,
        height,
        position: "relative",
        cursor: isFetching ? "not-allowed" : "pointer",
        borderRadius: "12px",
        overflow: "hidden",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Image */}
      <img
        src={finalUrl}
        alt={parsedFile.fileName || ""}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
          userSelect: "none",
          display: imageLoaded ? "block" : "none",
        }}
        draggable={false}
        onLoad={() => setImageLoaded(true)}
        onError={() => {
          setImageLoaded(false);
        }}
      />

      {/* Loading State - Show before image loads */}
      {!imageLoaded && !isFetching && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
          }}
        >
          {/* Background Pattern */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `
                repeating-linear-gradient(
                  45deg,
                  rgba(255, 255, 255, 0.03) 0px,
                  rgba(255, 255, 255, 0.03) 10px,
                  transparent 10px,
                  transparent 20px
                )
              `,
              pointerEvents: "none",
            }}
          />

          {/* Image Icon Circle with Eye/View Icon */}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.25)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="32"
              height="32"
              fill="white"
              style={{
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
              }}
            >
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </svg>
          </div>

          {/* File Name */}
          {parsedFile?.fileName && (
            <div
              style={{
                position: "relative",
                zIndex: 1,
                // background: "rgba(0, 0, 0, 0.3)",
                backdropFilter: "blur(5px)",
                padding: "6px 16px",
                borderRadius: "20px",
                maxWidth: "200px",
              }}
            >
              <p
                style={{
                  color: "white",
                  fontSize: "12px",
                  fontWeight: "500",
                  margin: 0,
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Tab to Load
              </p>
            </div>
          )}
        </div>
      )}

      {/* Hover Effect Overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0)",
          transition: "background 0.3s ease",
          pointerEvents: "none",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(0, 0, 0, 0.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(0, 0, 0, 0)";
        }}
      />
    </div>
  );
};

export const PDFWithOnClickFetch = ({ parsedFile, isMe }) => {

  const [isFetching, setIsFetching] = useState(false);
  const [hasError, setHasError] = useState(false);

  const openPdf = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleFetchAndOpen = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    if (isFetching) return;

    setIsFetching(true);
    setHasError(false);

    try {
      const fileName = extractBlobPath(parsedFile.mediaUrl);
      const res = await fetchSassTokenApi({ fileName });

      const newUrl =
        res?.uploadUrl ||
        res?.mediaUrl ||
        res?.url ||
        (res?.data &&
          (res.data.uploadUrl || res.data.mediaUrl || res.data.url));

      if (!newUrl) throw new Error("No SAS URL returned for PDF");

      openPdf(newUrl);
    } catch (err) {
      setHasError(true);
    } finally {
      setIsFetching(false);
    }
  };

  const onKeyboard = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleFetchAndOpen(e);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      style={{
        cursor: isFetching ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
      }}
      onClick={(e) => {
        console.log("test_pdf_wrapper_onClick");
        handleFetchAndOpen(e);
      }}
      onMouseDown={(e) => {
        console.log("test_pdf_onMouseDown");
        handleFetchAndOpen(e);
      }}
      onTouchStart={(e) => {
        console.log("test_pdf_onTouchStart");
        handleFetchAndOpen(e);
      }}
      onKeyDown={onKeyboard}
      className={`${isMe ? "text-white" : "text-primary"} text-sm text-decoration-none flex items-center`}
    >
      <i className="bi bi-file-earmark-pdf text-red-600 mr-1 fs-4" />
      {parsedFile.fileName}

      {/* Spinner overlay */}
      {isFetching && (
        <span className="ml-2">
          <Spinner size="sm" />
        </span>
      )}

      {/* Error display */}
      {hasError && (
        <span className="ml-2 text-red-500 text-xs">
          Failed — tap to retry
        </span>
      )}
    </div>
  );
};
